/* eslint-disable no-console -- This is a debugging plugin that monitors DOM operations */
/**
 * Vue-specific DOM patching for handling hydration and reactivity edge cases
 * Focuses on Vue's comment markers and text node operations
 *
 * NOTE: These errors are NOT reported to Sentry because they are:
 * - Already handled gracefully (caught, fallback applied)
 * - Unavoidable TOCTOU race conditions in Vue's DOM patching
 * - Not actionable - no fix possible for these race conditions
 * Console warnings and errorMap tracking remain for local debugging.
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (process.server) return

  console.log('[Vue DOM Patch] Initializing Vue-specific DOM protection...')

  const isDev = process.env.NODE_ENV !== 'production'
  const isVueComment = (node: Node): boolean => {
    return !!(
      node.nodeType === Node.COMMENT_NODE &&
      node.textContent &&
      (node.textContent.includes('[') ||
        node.textContent.includes(']') ||
        node.textContent === '')
    )
  }

  // Track errors by component/location
  const errorMap = new Map<string, number>()

  const getNodeDescription = (node: Node | null): string => {
    if (!node) return 'null'
    if (node.nodeType === Node.TEXT_NODE) {
      return `text("${node.textContent?.substring(0, 20)}...")`
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      return `comment("${node.textContent}")`
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      return `<${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${
        el.className ? `.${el.className.split(' ').join('.')}` : ''
      }>`
    }
    return `node(type:${node.nodeType})`
  }

  const getComponentContext = (): {
    name: string
    stack: string[]
    fullStack?: string
  } => {
    // Try to identify the Vue component from the stack trace
    try {
      const stack = new Error().stack || ''
      const lines = stack.split('\n')

      // Parse Vue component names from stack
      const vueComponents: string[] = []
      const stackLines: string[] = []

      lines.forEach((line) => {
        // Extract .vue file references
        const vueMatch = line.match(/(\w+)\.vue/)
        if (vueMatch && vueMatch[1]) {
          vueComponents.push(vueMatch[1])
        }
        // Clean up stack line for readability
        const cleanLine = line
          .trim()
          .replace(/^at /, '')
          .replace(/\s+\(.+\)$/, '')
        if (cleanLine) {
          stackLines.push(cleanLine)
        }
      })

      return {
        name: vueComponents[0] || 'unknown',
        stack: vueComponents,
        fullStack: isDev ? stack : undefined,
      }
    } catch {}
    return { name: 'unknown', stack: [], fullStack: undefined }
  }

  // Patch insertBefore
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(
    newChild: T,
    referenceChild: Node | null,
  ): T {
    // DEFENSIVE: Check parent is valid before attempting operation
    if (!this || !(this instanceof Node)) {
      console.warn(
        '[Vue DOM Patch] insertBefore called with null/invalid parent',
      )
      return newChild
    }

    // Capture parent state before operation for diagnostics
    const parentBeforeOperation = this
    const parentConnectedBefore = this.isConnected

    // TOCTOU NOTE: Race condition exists between this point and native insertBefore call
    // Parent could become null/disconnected during async operations
    // We handle this via try-catch rather than additional checks (which can't prevent it)
    try {
      return originalInsertBefore.call(this, newChild, referenceChild) as T
    } catch (lastError) {
      // Handled exception - NOT reported to Sentry (see module docstring)
      const component = getComponentContext()
      const errorKey = `${component.name}:insertBefore`
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      const parentConnectedAfter = parentBeforeOperation.isConnected

      console.warn('[Vue DOM Patch] insertBefore failed', {
        componentName: component.name,
        parent: getNodeDescription(this),
        newChild: getNodeDescription(newChild),
        referenceChild: getNodeDescription(referenceChild),
        parentConnectedBefore,
        parentConnectedAfter,
        ...(isDev ? { error: lastError } : {}),
      })

      // Intelligent fallback based on node types
      if (isVueComment(newChild)) {
        // Vue comment markers can be safely ignored
        return newChild
      }

      if (!this.isConnected) {
        // Parent is disconnected, operation would fail anyway
        return newChild
      }

      // Try appendChild as fallback for null reference
      if (!referenceChild) {
        try {
          return this.appendChild(newChild)
        } catch {
          // Silent fail
        }
      }

      return newChild
    }
  }

  // Patch removeChild
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T
    } catch (error) {
      const component = getComponentContext()
      const errorKey = `${component}:remove:${getNodeDescription(child)}`
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      if (count === 1) {
        console.warn(
          '[Vue DOM Patch] removeChild failed',
          {
            component,
            operation: 'removeChild',
            parent: getNodeDescription(this),
            child: getNodeDescription(child),
            parentConnected: this.isConnected,
          },
          isDev ? error : undefined,
        )
      }

      return child
    }
  }

  // Patch replaceChild
  const originalReplaceChild = Node.prototype.replaceChild
  Node.prototype.replaceChild = function <T extends Node>(
    newChild: Node,
    oldChild: T,
  ): T {
    try {
      return originalReplaceChild.call(this, newChild, oldChild) as T
    } catch (error) {
      const component = getComponentContext()
      console.warn(
        '[Vue DOM Patch] replaceChild failed',
        {
          component,
          operation: 'replaceChild',
          parent: getNodeDescription(this),
          newChild: getNodeDescription(newChild),
          oldChild: getNodeDescription(oldChild),
        },
        isDev ? error : undefined,
      )

      // Try to remove old and append new as fallback
      try {
        this.removeChild(oldChild)
        this.appendChild(newChild)
      } catch {
        // Silent fail
      }

      return oldChild
    }
  }

  // Development tools
  if (isDev && process.client) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Intentional window property for debugging
    ;(window as any).__vueDomPatch = {
      getErrors: () => {
        const errors = Array.from(errorMap.entries())
          .map(([key, count]) => ({ error: key, count }))
          .sort((a, b) => b.count - a.count)
        console.table(errors)
        return errors
      },
      clearErrors: () => {
        errorMap.clear()
        console.log('[Vue DOM Patch] Error map cleared')
      },
      disable: () => {
        Node.prototype.insertBefore = originalInsertBefore
        Node.prototype.removeChild = originalRemoveChild
        Node.prototype.replaceChild = originalReplaceChild
        console.log('[Vue DOM Patch] Disabled')
      },
    }

    // Log summary periodically in development
    setInterval(() => {
      if (errorMap.size > 0) {
        console.debug(
          `[Vue DOM Patch] ${errorMap.size} unique errors, ${Array.from(
            errorMap.values(),
          ).reduce((a, b) => a + b, 0)} total occurrences`,
        )
      }
    }, 60000) // Every minute
  }

  // Type guard for error-like objects
  const isErrorLike = (
    err: unknown,
  ): err is { message: string; stack?: string } => {
    return (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof (err as { message: unknown }).message === 'string'
    )
  }

  // Helper to handle null reference errors (Chrome/V8 format only)
  // Safari-style errors ("null is not an object") are NOT suppressed here
  // so we can verify if component-level fixes work
  const handleNullReferenceError = (error: unknown, event?: Event) => {
    if (!isErrorLike(error)) return false

    const msg = error.message
    // Check for null reference errors on DOM methods (Chrome/V8 format)
    if (
      msg.includes("Cannot read properties of null (reading 'insertBefore')") ||
      msg.includes("Cannot read properties of null (reading 'removeChild')") ||
      msg.includes("Cannot read properties of null (reading 'replaceChild')") ||
      msg.includes("Cannot read properties of null (reading 'appendChild')")
    ) {
      if (event) event.preventDefault() // Prevent Sentry from catching it

      const component = getComponentContext()
      const errorKey = `null-reference:${msg.match(/'(\w+)'/)?.[1] || 'unknown'}`
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      if (count === 1 || count === 10 || count === 100) {
        console.warn(
          `[Vue DOM Patch] Null reference error caught (${count}x)`,
          {
            component,
            error: msg,
            stack: isDev ? error.stack : undefined,
          },
        )
      }
      return true
    }
    return false
  }

  // Catch null reference errors from unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleNullReferenceError(event.reason, event)
  })

  // Also catch them from regular errors
  window.addEventListener(
    'error',
    (event) => {
      if (handleNullReferenceError(event.error, event)) {
        // Error was handled
        return
      }
    },
    true,
  )

  // Listen for route changes to clear error map
  nuxtApp.hook('app:mounted', () => {
    const router = nuxtApp.$router as
      | { afterEach?: (callback: () => void) => void }
      | undefined
    router?.afterEach?.(() => {
      // Clear error map on route change to track per-page issues
      if (errorMap.size > 10) {
        errorMap.clear()
      }
    })
  })

  // Verify patches are working
  const patchesApplied =
    Node.prototype.insertBefore !== originalInsertBefore &&
    Node.prototype.removeChild !== originalRemoveChild &&
    Node.prototype.replaceChild !== originalReplaceChild

  if (patchesApplied) {
    console.log(
      '[Vue DOM Patch] ✅ Vue DOM protection active - DOM operations protected against failures and null references',
    )
    console.log(
      '[Vue DOM Patch] 📊 Use window.__vueDomPatch.getErrors() to see any caught errors',
    )
  } else {
    console.error('[Vue DOM Patch] ❌ FAILED to apply some patches!')
  }
})
